import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/app/store";
import { Typography, toast } from "top-brains-ui";
import {
  Download,
  X,
  RefreshCw,
  AlertTriangle,
  Layers,
  CheckCircle,
} from "lucide-react";
import {
  removeFromQueue,
  enqueueVideoDownload,
  clearFailedTask,
  cancelCurrentDownload,
} from "@src/app/videoDownloadSlice";
import { isElectronApp } from "@src/utils/envUtils";
import {
  clearVideoCache,
  cancelActiveDownload,
} from "./GlobalVideoDownloader";

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const themePalette = {
  website: {
    surfaceBg: "#1a1a24",
    surfaceHover: "#262636",
    mutedSurfaceBg: "#232332",
    border: "#2e2e40",
    textPrimary: "#f0f0f5",
    textSecondary: "#8b8ba0",
    accent: "#5b5bf0",
    accentText: "#ffffff",
    boxShadow: "0 18px 36px rgba(0, 0, 0, 0.4)",
    errorRed: "#ef4444",
    successGreen: "#22c55e",
  },
  app: {
    surfaceBg: "#ffffff",
    surfaceHover: "#f8f8fc",
    mutedSurfaceBg: "#f4f4f8",
    border: "#e5e5ef",
    textPrimary: "#1a1a2e",
    textSecondary: "#6b7280",
    accent: "#4f46e5",
    accentText: "#ffffff",
    boxShadow: "0 18px 36px rgba(0, 0, 0, 0.12)",
    errorRed: "#ef4444",
    successGreen: "#10b981",
  },
};

export const HeaderDownloadMenu: React.FC = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const downloadState = useSelector((state: RootState) => state?.videoDownload);
  const currentTheme = useSelector(
    (state: RootState) => state?.globalSlice?.theme,
  );
  const accessToken = useSelector(
    (state: RootState) => state?.authSlice?.accessToken,
  );

  const isDarkTheme =
    currentTheme === "website-theme" || currentTheme === "editor-dark-theme";
  const activeTheme = isDarkTheme ? themePalette.website : themePalette.app;

  const isDownloading = downloadState?.isDownloading ?? false;
  const downloadProgress = downloadState?.downloadProgress ?? 0;
  const downloadedBytes = downloadState?.downloadedBytes ?? 0;
  const totalBytes = downloadState?.totalBytes ?? 0;
  const downloadStage = downloadState?.downloadStage ?? "";
  const currentTask = downloadState?.currentTask ?? null;
  const downloadQueue = downloadState?.downloadQueue ?? [];
  const failedTasks = downloadState?.failedTasks ?? {};
  // Deduplicate by task nodeId/targetId so identical failure stored under multiple keys only renders once
  const failedList = Object.values(
    Object.values(failedTasks).reduce(
      (acc, task) => {
        const key = task.nodeId || task.targetId;
        if (key) {
          acc[key] = task;
        }
        return acc;
      },
      {} as Record<string, (typeof failedTasks)[string]>,
    ),
  );

  const isElectron = isElectronApp();
  const hasFailed = failedList.length > 0;
  const hasActiveOrQueuedOrFailed =
    isDownloading ||
    Boolean(currentTask) ||
    downloadQueue.length > 0 ||
    hasFailed;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    // Keep popover closed by default when starting a new video download
    setIsOpen(false);
  }, [downloadState?.downloadingNodeId, downloadState?.currentTask?.timestamp]);

  // Show icon ONLY if video downloading/queued/failed and running in Electron app
  if (!isElectron || !hasActiveOrQueuedOrFailed) return null;

  const handleRetryFailed = (task: {
    nodeId: string;
    nodeName: string;
    targetId: string;
  }) => {
    dispatch(clearFailedTask(task.nodeId));
    if (task.targetId) {
      dispatch(clearFailedTask(task.targetId));
    }
    dispatch(
      enqueueVideoDownload({
        nodeId: task.nodeId,
        nodeName: task.nodeName,
        targetId: task.targetId,
        accessToken: accessToken || "",
        selectedTab: "Video",
      }),
    );
  };

  const handleRemoveQueueItem = async (nodeId: string) => {
    await cancelActiveDownload(nodeId);
    dispatch(removeFromQueue(nodeId));
    toast.info("Removed from download queue");
  };

  return (
    <div
      ref={menuRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      {/* Header Download Icon Button */}
      <span
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          padding: "4px",
          borderRadius: "6px",
          color: activeTheme.textPrimary,
          transition: "all 0.2s ease",
        }}
        title="Video Downloads"
      >
        <Download size={18} />

        {/* Badge Indicator */}
        {isDownloading && (
          <span
            style={{
              position: "absolute",
              top: "-5px",
              right: "-5px",
              background: activeTheme.accent,
              color: "#ffffff",
              fontSize: "9px",
              fontWeight: "bold",
              borderRadius: "10px",
              padding: "1px 4px",
              minWidth: "14px",
              textAlign: "center",
              lineHeight: "12px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            }}
          >
            {downloadProgress}%
          </span>
        )}

        {!isDownloading && hasFailed && (
          <span
            style={{
              position: "absolute",
              top: "-2px",
              right: "-2px",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: activeTheme.errorRed,
            }}
          />
        )}
      </span>

      {/* Popover Menubar */}
      {isOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "36px",
            right: 0,
            width: "340px",
            maxHeight: "480px",
            overflowY: "auto",
            backgroundColor: activeTheme.surfaceBg,
            background: activeTheme.surfaceBg,
            opacity: 1,
            isolation: "isolate",
            transform: "translateZ(99999px)",
            willChange: "transform",
            backdropFilter: "none",
            border: `1px solid ${activeTheme.border}`,
            borderRadius: "12px",
            boxShadow: activeTheme.boxShadow,
            zIndex: 99999,
            padding: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${activeTheme.border}`,
              paddingBottom: "8px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Download size={16} color={activeTheme.accent} />
              <Typography
                fontSize={14}
                fontWeight="bold"
                style={{ color: activeTheme.textPrimary }}
              >
                Video Downloads
              </Typography>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              style={{
                border: "none",
                background: "transparent",
                color: activeTheme.textSecondary,
                cursor: "pointer",
              }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Active Download Section */}
          {currentTask && (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: "8px",
                background: activeTheme.mutedSurfaceBg,
                border: `1px solid ${activeTheme.border}`,
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  fontSize={13}
                  fontWeight="bold"
                  style={{
                    color: activeTheme.textPrimary,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "200px",
                  }}
                >
                  {currentTask.nodeName}
                </Typography>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "bold",
                      color: activeTheme.accent,
                    }}
                  >
                    {downloadProgress}%
                  </span>
                  <button
                    type="button"
                    onClick={async () => {
                      await cancelActiveDownload(
                        currentTask.nodeId,
                        currentTask.targetId,
                      );
                      dispatch(cancelCurrentDownload());
                      toast.info("Download cancelled");
                    }}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: activeTheme.errorRed,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "2px",
                      borderRadius: "4px",
                    }}
                    title="Cancel download"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div
                style={{
                  height: "6px",
                  width: "100%",
                  background: activeTheme.border,
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${downloadProgress}%`,
                    background: activeTheme.accent,
                    transition: "width 0.2s ease",
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "11px",
                  color: activeTheme.textSecondary,
                }}
              >
                <span>
                  {downloadStage === "connecting" && "Connecting..."}
                  {downloadStage === "downloading" && "Downloading..."}
                  {downloadStage === "extracting" && "Extracting..."}
                  {downloadStage === "completed" && "Completed!"}
                </span>
                <span>
                  {formatBytes(downloadedBytes)}
                  {totalBytes > 0 ? ` / ${formatBytes(totalBytes)}` : ""}
                </span>
              </div>
            </div>
          )}



          {/* Failed Section */}
          {failedList.length > 0 && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <Typography
                fontSize={12}
                fontWeight="bold"
                style={{ color: activeTheme.errorRed }}
              >
                Failed Downloads ({failedList.length})
              </Typography>

              {failedList.map((task) => (
                <div
                  key={task.nodeId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 10px",
                    borderRadius: "6px",
                    background: "rgba(239, 68, 68, 0.08)",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      maxWidth: "180px",
                    }}
                  >
                    <AlertTriangle size={14} color={activeTheme.errorRed} />
                    <Typography
                      fontSize={12}
                      style={{
                        color: activeTheme.textPrimary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {task.nodeName}
                    </Typography>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRetryFailed(task)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "3px 8px",
                      borderRadius: "4px",
                      border: "1px solid rgba(239, 68, 68, 0.3)",
                      background: activeTheme.surfaceBg,
                      color: activeTheme.textPrimary,
                      fontSize: "11px",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                  >
                    <RefreshCw size={12} /> Retry
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!currentTask &&
            downloadQueue.length === 0 &&
            failedList.length === 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "24px 12px",
                  textAlign: "center",
                  gap: "8px",
                }}
              >
                <CheckCircle size={28} color={activeTheme.successGreen} />
                <Typography
                  fontSize={13}
                  style={{ color: activeTheme.textSecondary }}
                >
                  No active or queued video downloads.
                </Typography>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default HeaderDownloadMenu;
